import { motion } from "framer-motion";
import { Check, Landmark, HardHat, DraftingCompass } from "lucide-react";
import Watermark from "@/components/landing/Watermark";

const services = [
  {
    id: "gestion-integral",
    title: "Gestión Integral de Comunidades",
    tagline: "El control exhaustivo del inmueble con rigor ejecutivo y cero fricciones.",
    features: [
      { lead: "Juntas y Espacios Institucionales", text: "Convocatorias claras, actas impecables y ejecución estricta de acuerdos, con salas acondicionadas en centro de negocios y soporte para juntas telemáticas." },
      { lead: "Control económico y prevención de deuda", text: "Conciliación periódica, optimización de costes y gestión preventiva y extrajudicial de la morosidad." },
      { lead: "Supervisión técnica y portal del propietario", text: "Control in situ de instalaciones clave y registro documentado de cada aviso en la plataforma de vecinos, sin llamadas perdidas." },
    ],
    image: "/images/operativa.jpg",
    alt: "Operativa técnica y documental de AFGestiones",
    testId: "service-item-gestion-integral",
  },
  {
    id: "constitucion-comunidades",
    title: "Constitución y Puesta en Marcha de Comunidades",
    tagline: "Estructura sólida, legalidad y orden desde el primer día.",
    features: [
      { lead: "Legalización documental completa", text: "Legalización de libros de actas, obtención del CIF ante la Agencia Tributaria y alta de suministros generales." },
      { lead: "Estatutos y régimen interno", text: "Normas claras adaptadas a la realidad del edificio para prevenir conflictos de convivencia y de uso de zonas comunes." },
      { lead: "Junta constituyente y recepción técnica", text: "Convocatoria formal de la primera junta, nombramiento de órganos y coordinación de la entrega de instalaciones frente a la promotora." },
    ],
    image: "/images/edificio.jpg",
    alt: "Edificio residencial de nueva constitución",
    testId: "service-item-constitucion-comunidades",
  },
  {
    id: "asesoramiento-legal",
    title: "Asesoramiento Legal y Mediación Comunitaria",
    tagline: "Resolución de conflictos con elegancia administrativa y estricta seguridad jurídica.",
    features: [
      { lead: "Vía extrajudicial y ofertas vinculantes", text: "Protocolos formales de negociación previa antes de judicializar, acreditando siempre la buena fe procesal." },
      { lead: "Interpretación rigurosa de la LPH", text: "Asesoramiento preventivo al presidente y a la junta conforme a la normativa vigente y los coeficientes de participación." },
      { lead: "Mediación vecinal y defensa contractual", text: "Neutralidad para disolver tensiones internas y revisión exhaustiva de contratos y coberturas del edificio." },
    ],
    image: "/images/sello-metodologia.jpg",
    alt: "Documentación técnica y jurídica ordenada en un despacho sereno",
    testId: "service-item-asesoramiento-legal",
  },
];

const blindaje = [
  {
    icon: Landmark,
    title: "Seguridad Fiscal y Tributaria",
    text: "Supervisión y tramitación rigurosa ante la Agencia Tributaria Estatal y Canaria (declaraciones periódicas, retenciones a profesionales, liquidaciones de IGIC/IRPF y modelos informativos anuales). Cero sanciones por omisión administrativa.",
    testId: "blindaje-fiscal",
  },
  {
    icon: HardHat,
    title: "Coordinación Laboral y Prevención (PRL)",
    text: "Control normativo laboral y supervisión de la Coordinación de Actividades Empresariales (Prevención de Riesgos Laborales) de contratas concurrentes en zonas comunes, o gestión de personal propio.",
    testId: "blindaje-laboral",
  },
  {
    icon: DraftingCompass,
    title: "Supervisión Técnica y Enlace Colegiado",
    text: "Enlace directo con arquitectos, aparejadores e ingenieros para inspecciones técnicas reglamentarias (ITE/IEE y auditorías de OCAs). Fiscalización de obras conforme a proyecto y presupuesto.",
    testId: "blindaje-tecnico",
  },
];

export default function Servicios() {
  return (
    <section id="servicios" data-testid="servicios-section" className="relative bg-slate-50 py-8 sm:py-[clamp(2rem,3.5vw,3rem)] lg:py-12">
      <Watermark position="left" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-4xl mx-auto text-center mb-5 sm:mb-6 lg:mb-10"
        >
          <h2 className="font-display section-title text-slate-900" data-testid="servicios-title">
            Nuestros Servicios
          </h2>
          <p className="mt-3 max-w-[680px] mx-auto text-[clamp(1.1rem,1.4vw,1.25rem)] leading-[1.45] text-slate-500">
            Soluciones integrales de administración patrimonial, técnica y legal adaptadas a cada edificio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-4 lg:gap-7 items-stretch">
          {services.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col h-full"
            >
              <div className="overflow-hidden rounded-[12px] h-48 lg:h-[210px] w-full shadow-sm shrink-0">
                <motion.img
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  src={s.image}
                  alt={s.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="pt-5 flex flex-col flex-1">
                <h3 className="font-display card-title text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.tagline}</p>
                <ul className="mt-5 space-y-2">
                  {s.features.map((f) => (
                    <li key={f.lead} className="flex items-start gap-3 text-sm text-slate-600 leading-snug">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-slate-900" />
                      <span>
                        <strong className="font-semibold text-slate-900">{f.lead}:</strong> {f.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-[clamp(2.5rem,4vw,3.5rem)] w-full"
          data-testid="blindaje-section"
        >
          <h3 className="font-display section-title text-slate-900 max-w-3xl" data-testid="blindaje-title">
            Conexión Multidisciplinar y Blindaje de la Comunidad
          </h3>
          <p className="mt-3 text-slate-500 leading-relaxed max-w-2xl">
            Coordinamos los vértices fiscal, laboral y técnico para blindar al presidente y al edificio ante cualquier contingencia legal o administrativa.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-testid="blindaje-grid">
            {blindaje.map(({ icon: Icon, title, text, testId }, i) => (
              <motion.article
                key={testId}
                data-testid={testId}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
                className="group border border-slate-200 hover:border-slate-900 rounded-xl p-7 bg-white transition-colors duration-500"
              >
                <Icon className="w-6 h-6 text-slate-400 group-hover:text-slate-900 transition-colors duration-500 mb-8" />
                <h4 className="font-display card-title text-slate-900 leading-snug">{title}</h4>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">{text}</p>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
